/**
 * useExpiryCountdown — per-bubble disappearing-message countdown (Phase C).
 *
 * Returns a human-readable string like "0:42" or "23h" until `expiresAt`.
 * Calls `onExpired` exactly once when the local clock crosses the
 * expiry. Allows `grace` (default 60s) past `expiresAt` before firing
 * the callback to absorb minor clock drift between client/server (the
 * server is authoritative and will fire `MessageExpired` anyway, the
 * grace just keeps us inside `TTL+60s` per AC-11.1).
 *
 * Returns `null` when `expiresAt` is undefined or the message is
 * already expired and the callback has fired.
 */

import { useEffect, useRef, useState } from 'react';

const DEFAULT_GRACE_MS = 60_000;

function formatRemaining(ms: number): string {
  if (ms <= 0) return '0s';
  const sec = Math.ceil(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  if (min < 60) return remSec === 0 ? `${min}m` : `${min}:${String(remSec).padStart(2, '0')}`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const days = Math.floor(hr / 24);
  return `${days}d`;
}

export function useExpiryCountdown(
  expiresAt: string | undefined,
  onExpired?: () => void,
  graceMs: number = DEFAULT_GRACE_MS
): string | null {
  const [label, setLabel] = useState<string | null>(() =>
    expiresAt ? formatRemaining(new Date(expiresAt).getTime() - Date.now()) : null
  );
  // Stabilise onExpired so callers can pass an inline lambda without
  // tearing down the timer (and resetting firedRef) every render.
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
    if (!expiresAt) {
      setLabel(null);
      return;
    }

    const expiryMs = new Date(expiresAt).getTime();

    const tick = () => {
      const remaining = expiryMs - Date.now();
      if (remaining > 0) {
        setLabel(formatRemaining(remaining));
        return;
      }
      setLabel('0s');
      if (Date.now() >= expiryMs + graceMs && !firedRef.current) {
        firedRef.current = true;
        onExpiredRef.current?.();
      }
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, graceMs]);

  return label;
}
